import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import styled from "@emotion/styled";

import {
  Avatar as MuiAvatar,
  Badge,
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Popover as MuiPopover,
  SvgIcon,
  Tooltip,
  Typography,
  CircularProgress,
  Divider,
} from "@mui/material";
import { Bell, Home, UserPlus, Server, CheckCircle, ArrowRight } from "lucide-react";
import apiClient from "@utils/axiosInstance";
import { useAuth } from "@auth/AuthContext";

const Popover = styled(MuiPopover)`
  .MuiPaper-root {
    width: 300px;
    ${(props) => props.theme.shadows[1]};
    border: 1px solid ${(props) => props.theme.palette.divider};
  }
`;

const Indicator = styled(Badge)`
  .MuiBadge-badge {
    background: ${(props) => props.theme.header.indicator.background};
    color: ${(props) => props.theme.palette.common.white};
  }
`;

const Avatar = styled(MuiAvatar)`
  background: ${(props) => props.theme.palette.primary.main};
`;

const NotificationHeader = styled(Box)`
  text-align: center;
  border-bottom: 1px solid ${(props) => props.theme.palette.divider};
`;

const Notification = React.memo(function Notification({
  notification,
  onMarkAsRead,
}) {
  return (
    <ListItem
      divider
      sx={{
        bgcolor: notification.is_read ? 'transparent' : 'action.hover',
        alignItems: 'flex-start',
        py: 1.5,
        flexDirection: 'column',
      }}
    >
      <Box display="flex" width="100%" alignItems="flex-start">
        <ListItemAvatar>
          <Avatar>
            <SvgIcon fontSize="small">
              <Bell />
            </SvgIcon>
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={notification.title}
          primaryTypographyProps={{
            variant: "subtitle2",
            color: "textPrimary",
            fontWeight: notification.is_read ? "normal" : "bold",
          }}
          secondary={
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {notification.message}
              </Typography>
              <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>
                {new Date(notification.created_at).toLocaleString()}
              </Typography>
            </>
          }
        />
        {!notification.is_read && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onMarkAsRead(notification.id);
            }}
            title="Mark as read"
            sx={{ mt: 1 }}
          >
            <SvgIcon fontSize="small">
              <CheckCircle />
            </SvgIcon>
          </IconButton>
        )}
      </Box>
      {notification.link && (
        <Box ml={7} mt={1}>
          <Button
            component={Link}
            to={notification.link}
            size="small"
            variant="outlined"
            endIcon={
              <SvgIcon fontSize="small">
                <ArrowRight />
              </SvgIcon>
            }
            sx={{ textTransform: 'none' }}
          >
            View Details
          </Button>
        </Box>
      )}
    </ListItem>
  );
});

const NavbarNotificationsDropdown = React.memo(
  function NavbarNotificationsDropdown() {
    const ref = useRef(null);
    const [isOpen, setOpen] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [recentNotifications, setRecentNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const { isAuthenticated } = useAuth();

    // Poll for unread count every 30 seconds — only when authenticated
    useEffect(() => {
      if (!isAuthenticated) return;
      loadUnreadCount();
      const interval = setInterval(loadUnreadCount, 30000);
      return () => clearInterval(interval);
    }, [isAuthenticated]);

    const loadUnreadCount = async () => {
      try {
        const res = await apiClient.get('/notifications/unread-count');
        setUnreadCount(res.data.unread_count || 0);
      } catch (err) {
        // 401 is a known race with stale-token signout; AuthContext clears state globally.
        if (err?.response?.status !== 401) {
          console.error('Failed to load unread count:', err);
        }
      }
    };

    const loadRecentNotifications = async () => {
      setLoading(true);
      try {
        const res = await apiClient.get('/notifications?per_page=5');
        setRecentNotifications(res.data.data || []);
      } catch (err) {
        console.error('Failed to load notifications:', err);
      } finally {
        setLoading(false);
      }
    };

    const handleMarkAsRead = async (id) => {
      try {
        await apiClient.post(`/notifications/${id}/mark-read`);
        setRecentNotifications(prev =>
          prev.map(n => (n.id === id ? { ...n, is_read: true } : n))
        );
        loadUnreadCount();
      } catch (err) {
        console.error('Failed to mark as read:', err);
      }
    };

    const handleOpen = () => {
      setOpen(true);
      loadRecentNotifications();
    };

    const handleClose = () => {
      setOpen(false);
    };

    return (
      <React.Fragment>
        <Tooltip title="Notifications">
          <IconButton
            color="inherit"
            ref={ref}
            onClick={handleOpen}
            size="large"
          >
            <Indicator badgeContent={unreadCount}>
              <Bell />
            </Indicator>
          </IconButton>
        </Tooltip>
        <Popover
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          anchorEl={ref.current}
          onClose={handleClose}
          open={isOpen}
        >
          <NotificationHeader p={2}>
            <Typography variant="subtitle2" color="textPrimary">
              Notifications
              {unreadCount > 0 && (
                <Typography
                  component="span"
                  variant="body2"
                  color="text.secondary"
                  ml={1}
                >
                  ({unreadCount} unread)
                </Typography>
              )}
            </Typography>
          </NotificationHeader>
          <React.Fragment>
            {loading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress size={24} />
              </Box>
            ) : recentNotifications.length === 0 ? (
              <Box p={3} textAlign="center">
                <Typography color="text.secondary" variant="body2">
                  No notifications yet
                </Typography>
              </Box>
            ) : (
              <List disablePadding sx={{ maxHeight: 400, overflow: 'auto' }}>
                {recentNotifications.map((notification) => (
                  <Notification
                    key={notification.id}
                    notification={notification}
                    onMarkAsRead={handleMarkAsRead}
                  />
                ))}
              </List>
            )}
            <Divider />
            <Box p={1} display="flex" justifyContent="center">
              <Button size="small" component={Link} to="/notifications">
                View All Notifications
              </Button>
            </Box>
          </React.Fragment>
        </Popover>
      </React.Fragment>
    );
  }
);

export default NavbarNotificationsDropdown;
